import React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import { isValid as isDateValid } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders,
  SignupResultStatus
} from "../../interfaces";

import {
  GuestSignup
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  GuestType,
  NULL_DATETIME_STRING,
  NULL_DATETIME_TIMEZONE_STRING,
  NULL_ID,
  SignupExceptionEnum
} from '../../global';

import GuestTypeSelect from './GuestTypeSelect';
import GuestGenderSelect from './GuestGenderSelect';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// UTC to local date formatting
import { useDates } from '../../hooks/UseDates';

// Signup date criteria
import { useSignupDates } from '../../hooks/UseSignupDates';

// Redux
import { useAppSelector } from '../../hooks/UseReduxState';
import { getRequestMaxDeparture, getRequestMinArrival } from '../../../redux/requestSlice';

interface GuestRequestEditProps {
  guestSignup: GuestSignup;
  handleClose: () => void;
  handleUpdate: (signup: GuestSignup) => void;
}

const GuestRequestEdit: React.FC<GuestRequestEditProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { enqueueSnackbar } = useSnackbar();
  const { dateFormatted } = useDates();
  const { calcMaxArrival, calcMaxDeparture, calcMinArrival, calcMinDeparture } = useSignupDates();
  const { dateStringToLocalDateFormatted } = useDates();

  const [enableSave, setEnableSave] = React.useState(false);
  const [guestSignup, setGuestSignup] = React.useState<GuestSignup>(new GuestSignup());
  const [maxArrival, setMaxArrival] = React.useState<string>(EMPTY_STRING);
  const [maxDeparture, setMaxDeparture] = React.useState<string>(EMPTY_STRING);
  const [minArrival, setMinArrival] = React.useState<string>(EMPTY_STRING);
  const [minDeparture, setMinDeparture] = React.useState<string>(EMPTY_STRING);

  // Standardized dates from member in Redux
  const defaultDeparture: string = useAppSelector(getRequestMaxDeparture);
  const defaultArrival: string = useAppSelector(getRequestMinArrival);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(1)
    },
    actions: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    rowSpacer: {
      margin: theme.spacing(2, 0, 2, 0)
    },
    requestDate: {
      margin: theme.spacing(0, 0, 0, 0)
    },
    updated: {
      margin: theme.spacing(1, 0, 0, 0)
    },
    updatedBy: {
      margin: theme.spacing(0, 0, 0, 0)
    },
    waiver: {
      margin: theme.spacing(1, 0, 0, 0)
    }
  };

  // #endregion

  // #region Methods

  /*
 * Check if updated request dates contain priority period
 */
  const checkPriorityDates = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // URL parameters
    const params = new URLSearchParams();
    params.append('signupID', guestSignup.signupID.toString());
    params.append('arrivalDate', guestSignup.arrivalDate);
    params.append('departureDate', guestSignup.departureDate);

    const url = new URL(`${API_URL}/v1/requests/areupdatedatespriority`);
    url.search = params.toString();

    return await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to check if updated dates are in a priority period');
        }
        return response.json()
      })
      .then((sexc: SignupResultStatus) => {
        return (sexc.statusCode === SignupExceptionEnum.RequestDatesInPriorityPeriod);
      })
      .catch(err => {
        return false;
      }
      );
  }

  /*
   * Update guest data in parent guest list
   */
  const save = async () => {

    setEnableSave(false);

    // Determine if the dates are w/in a priority period if max reached
    if (await checkPriorityDates()) {
      enqueueSnackbar('Request dates can not include a priority period since the maximum priority requests has been reached.', { variant: 'warning', autoHideDuration: 7500 });
    } else {
      props.handleUpdate(guestSignup);
    }

  }


  /*
   * Validate if all data is present and if any values have changed
   */
  const validate = () => {

    let save = false;

    if (
      (
        (guestSignup.arrivalDate !== NULL_DATETIME_STRING)
        && (guestSignup.departureDate !== NULL_DATETIME_STRING)
        && (guestSignup.firstName !== EMPTY_STRING)
        && (guestSignup.lastName !== EMPTY_STRING)
        && (guestSignup.gender !== EMPTY_STRING)
        && (guestSignup.guestType.guestTypeID !== NULL_ID)
      )
      &&
      (
        (guestSignup.arrivalDate !== props.guestSignup.arrivalDate)
        || (guestSignup.departureDate !== props.guestSignup.departureDate)
        || (guestSignup.firstName !== props.guestSignup.firstName)
        || (guestSignup.lastName !== props.guestSignup.lastName)
        || (guestSignup.email !== props.guestSignup.email)
        || (guestSignup.gender !== props.guestSignup.gender)
        || (guestSignup.guestType.guestTypeID !== props.guestSignup.guestType.guestTypeID)
        || (guestSignup.priorityGuest !== props.guestSignup.priorityGuest)
      )
    ) {
      // Adults must have email
      if (guestSignup.guestType.guestTypeID === GuestType.Adult) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        save = (guestSignup.email !== EMPTY_STRING) && emailRegex.test(guestSignup.email);
      } else {
        save = true;
      }
    }

    setEnableSave(save);
  }

  // #endregion

  // #region Event handlers

  /*
  * Event handler for changes to arrival date
  */
  const handleArrivalDateChange = (date: Date | null) => {

    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    const formattedDate = date ? dateFormatted(date) : EMPTY_STRING;

    if (formattedDate) {

      // Arrival must be today or greater
      const arrivalDate: Date = new Date(formattedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (arrivalDate < today) {
        enqueueSnackbar(`Arrival date must be today or later`, { variant: 'warning' });
        return;
      }

      // Departure date exists
      if (guestSignup.departureDate) {
        // Check if arrival >= departure date
        const arrivalDate: Date = new Date(formattedDate);
        const departureDate: Date = new Date(guestSignup.departureDate);
        if (arrivalDate >= departureDate) {
          enqueueSnackbar(`Arrival date must be earlier than departure date`, { variant: 'warning' });
        } else {
          // Set new arrival date
          setGuestSignup({
            ...guestSignup,
            arrivalDate: formattedDate
          });
        }
      } else {
        // Departure date is empty, set +1
        const plusDate: Date = new Date(formattedDate);
        plusDate.setDate(plusDate.getDate() + 1);
        // Set new arrival and departure date
        setGuestSignup({
          ...guestSignup,
          arrivalDate: formattedDate,
          departureDate: plusDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })
        });
      }
    } else {
      validate();
    }

  };

  /*
   * Event handler for changes to departure date
   */
  const handleDepartureDateChange = (date: Date | null) => {

    // Test valid date format
    if (!isDateValid(date)) {
      return;
    }

    const formattedDate = date ? dateFormatted(date) : EMPTY_STRING;

    if (formattedDate) {
      const departureDate: Date = new Date(formattedDate);

      // Departure must be tomorrow or greater
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date();
      yesterday.setHours(0, 0, 0, 0);
      yesterday.setDate(yesterday.getDate() - 1);

      if ((departureDate < today)) {
        enqueueSnackbar(`Departure date must be tomorrow or later`, { variant: 'warning' });
        return;
      }

      // Arrival date exists
      if (guestSignup.arrivalDate) {
        // Check if arrival >= departure date
        const departureDate: Date = new Date(formattedDate);
        const arrivalDate: Date = new Date(guestSignup.arrivalDate);
        if (departureDate <= arrivalDate) {
          enqueueSnackbar(`Departure date must be later than arrival date`, { variant: 'warning' });
        } else {
          // Set new departure date
          setGuestSignup({
            ...guestSignup,
            departureDate: formattedDate
          });
        }
      } else {
        // Arrival date is empty, set -1
        const minusDate: Date = new Date(formattedDate);
        minusDate.setDate(minusDate.getDate() - 1);
        setGuestSignup({
          ...guestSignup,
          arrivalDate: minusDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          departureDate: formattedDate
        });
      }
    } else {
      validate();
    }

  };

  /*
   * Event handler for changes to guest email
   */
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGuestSignup({
      ...guestSignup,
      email: event.target.value
    });
  }

  /*
   * Event handler for changes to guest first name
   */
  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGuestSignup({
      ...guestSignup,
      firstName: event.target.value
    });
  }

  /*
   * Event handler for changes to guest gender
   */
  const handleGenderChange = (gender: string) => {
    setGuestSignup({
      ...guestSignup,
      gender: gender
    });
  }

  /*
   * Event handler for changes to guest type
   */
  const handleGuestTypeChange = (id: number, name: string) => {
    //// Clone guest type then change value
    const gt = { ...guestSignup.guestType };
    gt.guestTypeID = id;
    gt.name = name;

    // Set cloned guest type
    setGuestSignup({
      ...guestSignup,
      email: gt === GuestType.Adult ? guestSignup.email : EMPTY_STRING,
      guestType: gt
    });

  }

  /*
   * Event handler for changes to guest last name
   */
  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGuestSignup({
      ...guestSignup,
      lastName: event.target.value
    });
  }

  /*
   * Event handler for changes to priority guest
   */
  const handlePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    setGuestSignup({
      ...guestSignup,
      priorityGuest: event.target.checked
    });
  }

  React.useEffect(() => {
    // Load guest data
    if (!props.guestSignup.arrivalDate || !props.guestSignup.departureDate) {
      // Set default dates if not present
      const newSignup = { ...props.guestSignup };
      newSignup.arrivalDate = defaultArrival;
      newSignup.departureDate = defaultDeparture;
      setGuestSignup(newSignup);
    } else {
      setGuestSignup({ ...props.guestSignup });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.guestSignup.guestSignupID]);

  React.useEffect(() => {
    if (guestSignup.guestSignupID !== NULL_ID) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(guestSignup)]);

  React.useEffect(() => {
    if (guestSignup.departureDate) {
      setMaxArrival(calcMaxArrival(guestSignup.departureDate));
      setMinArrival(calcMinArrival(guestSignup.departureDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestSignup.departureDate]);

  React.useEffect(() => {
    if (guestSignup.arrivalDate) {
      setMaxDeparture(calcMaxDeparture(guestSignup.arrivalDate));
      setMinDeparture(calcMinDeparture(guestSignup.arrivalDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestSignup.arrivalDate]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container alignItems="center">
        <Grid>
          <IconButton onClick={props.handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid>
          <Typography variant="h6">Guest</Typography>
        </Grid>
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <TextField
          label="First Name"
          value={guestSignup.firstName}
          fullWidth
          required
          onChange={handleFirstNameChange}
          autoFocus
        />
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <TextField
          label="Last Name"
          value={guestSignup.lastName}
          fullWidth
          required
          onChange={handleLastNameChange}
        />
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <GuestGenderSelect
          gender={guestSignup.gender}
          genderSelected={handleGenderChange} />
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <GuestTypeSelect guestTypeID={guestSignup.guestType.guestTypeID} guestTypeChanged={handleGuestTypeChange} />
      </Grid>
      {(guestSignup.guestType.guestTypeID === GuestType.Adult) &&
        <Grid container sx={styles.rowSpacer}>
          <TextField
            type="email"
            name="email"
            label="Email"
            value={guestSignup.email}
            fullWidth
            onChange={handleEmailChange}
            required={true}
          />
        </Grid>
      }
      <Grid container sx={styles.waiver}>
        <Typography variant="body2"><i>Adult guests will receive an email to sign a waiver</i></Typography>
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <DatePicker
          disablePast
          format="MM/dd/yyyy"
          label="Arrival"
          minDate={minArrival ? new Date(minArrival) : undefined}
          maxDate={maxArrival ? new Date(maxArrival) : undefined}
          onChange={handleArrivalDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          value={guestSignup.arrivalDate === EMPTY_STRING ? null : new Date(guestSignup.arrivalDate)}
          views={['month', 'day']}
        />
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <DatePicker
          format="MM/dd/yyyy"
          label="Departure"
          minDate={minDeparture ? new Date(minDeparture) : undefined}
          maxDate={maxDeparture ? new Date(maxDeparture) : undefined}
          onChange={handleDepartureDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          value={guestSignup.departureDate === EMPTY_STRING ? null : new Date(guestSignup.departureDate)}
          views={['month', 'day']}
        />
      </Grid>
      <Grid container sx={styles.rowSpacer}>
        <FormControlLabel control={<Checkbox checked={guestSignup.priorityGuest} onChange={handlePriorityChange} />} label="Priority Guest" />
      </Grid>
      <Grid container justifyContent="center" sx={styles.actions}>
        <Tooltip title="Add or update guest in request">
          <span>
            <Button variant="contained" color="primary" disabled={!enableSave} onClick={save}>Save</Button>
          </span>
        </Tooltip>
      </Grid>
      {((guestSignup.updated !== NULL_DATETIME_TIMEZONE_STRING) || guestSignup.updatedBy || (guestSignup.signupDate !== NULL_DATETIME_TIMEZONE_STRING)) &&
        <Divider />
      }
      {guestSignup.updated !== NULL_DATETIME_TIMEZONE_STRING &&
        <Grid container sx={styles.updated}>
          <Typography variant="body2">{`Updated: ${dateStringToLocalDateFormatted(guestSignup.updated)}`}</Typography>
        </Grid>
      }
      {guestSignup.updatedBy &&
        <Grid container sx={styles.updatedBy}>
          <Typography variant="body2">{`By ${guestSignup.updatedBy ? guestSignup.updatedBy : 'n/a'}`}</Typography>
        </Grid>
      }
      {guestSignup.signupDate !== NULL_DATETIME_TIMEZONE_STRING &&
        <Grid container sx={styles.requestDate}>
          <Typography variant="body2">{`Requested: ${dateStringToLocalDateFormatted(guestSignup.signupDate)}`}</Typography>
        </Grid>
      }
    </Box>
  );

}

export default GuestRequestEdit;